/*
 * Slide Templates — the Asset Library's "Slide Templates" tab content.
 *
 * Fetches the template manifest (services/slideTemplatesApi.ts) and renders
 * it as a preview grid, alongside a "Filter by" sidebar built from the
 * manifest's own `category` field (e.g. "Bio", "Chart") — not a separate
 * curated taxonomy, and there's no favourites concept. Category filtering and
 * the client-side name/category/layoutClass search narrow the grid together.
 *
 * THE CARDS
 * The manifest carries no rendered thumbnails, so a card would otherwise be an
 * empty grey box — a hundred-odd identical ones. Instead each card draws the
 * entry's placeholder rectangles (normalised into `template.preview` by the
 * api module) as a role-coloured wireframe, which is a real, distinguishable
 * picture of the layout. A plain placeholder box is only used for entries with
 * no usable geometry at all.
 *
 * An entry is only insertable when its manifest record carries a `pptxFile`
 * (see services/slideTemplatesApi.ts) — most do, but a stale/malformed one
 * won't. Badging every card "Preview only" would be noise on a set that's
 * almost entirely insertable, so the "none insertable" case gets one notice
 * above the grid instead; the per-card badge appears only in a mixed set,
 * where it actually distinguishes one card from another.
 *
 * The "Insert into deck" action lives in the shared AssetLibraryContent
 * footer, not here, so this component exposes an imperative `insertSelected`
 * handle (via ref) plus a selection-state callback so that footer can drive
 * this panel's insert flow and reflect its selection count/button state.
 */

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useSlideTemplateThumbnail } from "../hooks/useSlideTemplateThumbnail";
import { useSlideTemplates, type UseSlideTemplatesOptions } from "../hooks/useSlideTemplates";
import type { SlidePreview, SlidePreviewRole, SlideTemplate } from "../services/slideTemplatesApi";
import type { ExtractedSlideImage } from "../../shared/services/pptxSlideImageExtraction";
import { Notice } from "../../../../../shared/components/Notice";
import { FilesIcon, WarningIcon } from "../../../../../shared/components/icons";
import { SearchIcon } from "../../action-chips/chipIcons";
import { useRequestNewSelection } from "../../asset-library/types";
import styles from "./SlideTemplatesPanel.module.css";

/** What the tab is showing, so the shared footer can word itself to match. */
export type SlideTemplatesView = "ready" | "error" | "empty";

export interface SlideTemplatesSelectionState {
  readonly count: number;
  readonly canInsert: boolean;
  readonly isInserting: boolean;
  readonly view?: SlideTemplatesView;
}

export interface SlideTemplatesPanelHandle {
  insertSelected: () => Promise<void>;
  /** Deselects every template (the footer's "Clear All"). */
  clearSelection: () => void;
}

interface SlideTemplatesPanelProps {
  readonly onSelectionStateChange?: (state: SlideTemplatesSelectionState) => void;
  /**
   * Where the manifest comes from and how a selection is committed — see
   * hooks/useSlideTemplates.ts. Omit it for the in-pane Asset Library (fetch +
   * insert locally); the Office Dialog window passes the hand-off/messageParent
   * pair instead, since it has neither a bearer token nor PowerPoint.run().
   */
  readonly templatesOptions?: UseSlideTemplatesOptions;
}

const PREVIEW_ONLY_HINT = "This template can be previewed but not inserted yet";

const SEARCH_PLACEHOLDER = "Try '4 columns', 'compare', 'team structure', 'timeline'…";

/**
 * Every whitespace-separated word of the query must appear somewhere in the
 * template's name, category, layout class or description, so "team structure"
 * matches a card that has "team" in its name and "structure" in its category.
 */
function matchesSearch(template: SlideTemplate, query: string): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const haystack =
    `${template.name} ${template.category ?? ""} ${template.layoutClass ?? ""} ${template.description ?? ""}`.toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

/** Per-role class, so the wireframe reads as a layout and not a grey mosaic. */
const ROLE_CLASS: Readonly<Record<SlidePreviewRole, string | undefined>> = {
  title: styles.blockTitle,
  body: styles.blockBody,
  image: styles.blockImage,
  chart: styles.blockChart,
  table: styles.blockTable,
  other: styles.blockOther,
};

/**
 * The placeholder rectangles, drawn as a wireframe.
 *
 * The viewBox is a flat 0–100 square with `preserveAspectRatio="none"`: blocks
 * are already percentages of their own axis, so letting each axis stretch
 * independently to the (correctly-proportioned) box is what puts them in the
 * right place. Only the 1-unit corner radius distorts, which is invisible at
 * thumbnail size.
 */
function LayoutWireframe({ preview }: Readonly<{ preview: SlidePreview }>) {
  return (
    <svg className={styles.wireframe} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
      {preview.blocks.map((block, index) => (
        <rect
          // Blocks have no ids of their own; the rect's own geometry is what
          // makes it unique within a card (identical ones are deduped upstream).
          key={`${block.role}-${block.x}-${block.y}-${block.w}-${block.h}-${index}`}
          className={ROLE_CLASS[block.role] ?? ROLE_CLASS.other}
          x={block.x}
          y={block.y}
          width={block.w}
          height={block.h}
          rx={1}
        />
      ))}
    </svg>
  );
}

/**
 * Manifest thumbnail, wireframe, or a bare document-icon placeholder — in
 * that priority order. `hasPhoto` suppresses the icon once a real photo is
 * about to be layered on top (see TemplateThumbnail): a card with nothing
 * left to draw behind a photo that already fills it shouldn't show one.
 */
function ThumbnailBaseLayer({
  template,
  preview,
  hasPhoto,
}: Readonly<{ template: SlideTemplate; preview: SlidePreview | null; hasPhoto: boolean }>) {
  if (template.thumbnailUrl) {
    return <img className={styles.thumbnailImg} src={template.thumbnailUrl} alt="" loading="lazy" />;
  }
  if (preview) {
    return <LayoutWireframe preview={preview} />;
  }
  if (hasPhoto) {
    return null;
  }
  return (
    <div className={styles.thumbnailFallback} aria-hidden="true">
      <span>🗎</span>
    </div>
  );
}

/**
 * Base layer (manifest thumbnail, wireframe, or a bare placeholder) plus, once
 * the card scrolls into view, the slide's own real embedded photo(s) laid on
 * top — see hooks/useSlideTemplateThumbnail.ts for why this is lazy and
 * ../shared/services/pptxSlideImageExtraction.ts for why it isn't sourced from the manifest.
 */
function TemplateThumbnail({
  template,
  loadThumbnail,
}: Readonly<{
  template: SlideTemplate;
  loadThumbnail?: (template: SlideTemplate) => Promise<readonly ExtractedSlideImage[]>;
}>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const images = useSlideTemplateThumbnail(template, rootRef, loadThumbnail);

  // Once a card has its real photo(s), the wireframe's "image"-role rects
  // are just a stale placeholder for the same region — drawing both reads as
  // a jumbled mix of real content and abstract blocks, not a clean preview.
  const wireframePreview = useMemo(() => {
    if (images.length === 0 || !template.preview) return template.preview;
    const blocks = template.preview.blocks.filter((block) => block.role !== "image");
    return blocks.length > 0 ? { ...template.preview, blocks } : null;
  }, [images.length, template.preview]);

  return (
    <div ref={rootRef} className={styles.thumbnailStack}>
      <ThumbnailBaseLayer template={template} preview={wireframePreview} hasPhoto={images.length > 0} />
      {images.map((image, index) => (
        <img
          // Extracted fresh per render from the slide's own picture list —
          // there's no stable id, and position + source together are unique.
          key={`${image.xPct}-${image.yPct}-${index}`}
          className={styles.thumbnailPhoto}
          src={image.dataUrl}
          alt=""
          style={{ left: `${image.xPct}%`, top: `${image.yPct}%`, width: `${image.wPct}%`, height: `${image.hPct}%` }}
        />
      ))}
    </div>
  );
}

/** A search that matched nothing — says so and offers the way back (BRD 8.10). */
function NoResults({ query, onClear }: Readonly<{ query: string; onClear: () => void }>) {
  return (
    <Notice
      icon={<SearchIcon />}
      title={`No results for “${query}”`}
      message="Try a different word, or clear the search to see everything."
      action={
        <button type="button" className={styles.retryBtn} onClick={onClear}>
          Clear Search
        </button>
      }
    />
  );
}

function TemplateCard({
  template,
  selected,
  showPreviewOnlyBadge,
  onToggle,
  loadThumbnail,
}: Readonly<{
  template: SlideTemplate;
  selected: boolean;
  showPreviewOnlyBadge: boolean;
  onToggle: (id: string) => void;
  loadThumbnail?: (template: SlideTemplate) => Promise<readonly ExtractedSlideImage[]>;
}>) {
  const toggle = () => onToggle(template.id);

  return (
    <button
      type="button"
      className={selected ? `${styles.card} ${styles.cardSelected}` : styles.card}
      aria-pressed={selected}
      title={template.description ?? template.name}
      onClick={toggle}
    >
      <div className={styles.thumbnail}>
        <div className={styles.thumbnailInner}>
          <TemplateThumbnail template={template} loadThumbnail={loadThumbnail} />
        </div>
        {selected ? (
          <span className={styles.selectedTick} aria-hidden="true">
            ✓
          </span>
        ) : (
          <span className={styles.unselectedCircle} aria-hidden="true" />
        )}
        {/* `insertable`, not `sourceFileUrl`/`slideIndex` — the dialog's copy
            of the manifest has neither field at all (see
            asset-library/services/assetLibraryDialogChannel.ts). */}
        {showPreviewOnlyBadge && (
          <span className={styles.previewOnlyBadge} title={PREVIEW_ONLY_HINT}>
            Preview only
          </span>
        )}
      </div>
      <div className={styles.cardFooter}>
        <span className={styles.cardName}>{template.name}</span>
      </div>
    </button>
  );
}

interface CategoryCount {
  readonly label: string;
  readonly count: number;
}

/**
 * "FILTER BY" sidebar — categories are the manifest's own `category` field
 * (e.g. "Bio", "Chart"), the same value each card's chip now shows; there's
 * no separate taxonomy or favourites concept behind this, by design.
 */
function CategoryFilterSidebar({
  categories,
  totalCount,
  activeCategory,
  onSelect,
}: Readonly<{
  categories: readonly CategoryCount[];
  totalCount: number;
  activeCategory: string | null;
  onSelect: (category: string | null) => void;
}>) {
  return (
    <nav className={styles.filterSidebar} aria-label="Filter slide templates by category">
      <div className={styles.filterHeading}>Filter by</div>
      <ul className={styles.filterList}>
        <FilterRow label="All" count={totalCount} active={activeCategory === null} onSelect={() => onSelect(null)} />
        {categories.map((category) => (
          <FilterRow
            key={category.label}
            label={category.label}
            count={category.count}
            active={activeCategory === category.label}
            onSelect={() => onSelect(category.label)}
          />
        ))}
      </ul>
    </nav>
  );
}

function FilterRow({
  label,
  count,
  active,
  onSelect,
}: Readonly<{ label: string; count: number; active: boolean; onSelect: () => void }>) {
  return (
    <li>
      <button
        type="button"
        className={active ? `${styles.filterItem} ${styles.filterItemActive}` : styles.filterItem}
        aria-pressed={active}
        onClick={onSelect}
      >
        <span className={styles.filterLabel}>{label}</span>
        <span className={styles.filterCount}>{count}</span>
      </button>
    </li>
  );
}

export const SlideTemplatesPanel = forwardRef<SlideTemplatesPanelHandle, SlideTemplatesPanelProps>(
  function SlideTemplatesPanel({ onSelectionStateChange, templatesOptions }, ref) {
    const {
      loadState,
      insertStatus,
      templates,
      selectedIds,
      selectedCount,
      toggleSelected,
      clearSelected,
      retry,
      insertSelected,
      isInserting,
      insertError,
    } = useSlideTemplates(templatesOptions);
    const loadThumbnail = templatesOptions?.loadThumbnail;
    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

    const trimmedSearch = search.trim();

    // The search narrows everything — the grid, the match line and every
    // sidebar count — so they all read from the same list.
    const searchMatches = useMemo(
      () => templates.filter((template) => matchesSearch(template, trimmedSearch)),
      [templates, trimmedSearch],
    );

    // Sidebar options, built from the manifest's own `category` field — the
    // same value each card's chip shows. The row set and its order come from
    // the full manifest (so an active category never disappears mid-search);
    // only the counts follow the current search.
    const categoryCounts = useMemo(() => {
      const totals = new Map<string, number>();
      for (const template of templates) {
        if (!template.category) continue;
        totals.set(template.category, (totals.get(template.category) ?? 0) + 1);
      }
      const matching = new Map<string, number>();
      for (const template of searchMatches) {
        if (!template.category) continue;
        matching.set(template.category, (matching.get(template.category) ?? 0) + 1);
      }
      return Array.from(totals, ([label, total]) => ({ label, total, count: matching.get(label) ?? 0 }))
        .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label))
        .map(({ label, count }) => ({ label, count }));
    }, [templates, searchMatches]);

    const visibleTemplates = useMemo(
      () => searchMatches.filter((template) => categoryFilter === null || template.category === categoryFilter),
      [searchMatches, categoryFilter],
    );

    // Every card badged "Preview only" says nothing; one notice above the grid
    // does. Only badge cards when the set is actually mixed.
    const noneInsertable = useMemo(
      () => templates.length > 0 && templates.every((template) => !template.insertable),
      [templates],
    );

    const matchLabel = searchMatches.length === 1 ? "template match" : "templates match";
    const matchLineText = `${searchMatches.length} ${matchLabel} “${trimmedSearch}”`;

    const clearSearch = () => setSearch("");

    let view: SlideTemplatesView = "ready";
    if (loadState.status === "error") view = "error";
    else if (loadState.status === "ready" && templates.length === 0) view = "empty";

    // Removing a pick is always free; adding one may need the user's go-ahead
    // (another tab can still hold uninserted picks — BRD 8.9).
    const requestNewSelection = useRequestNewSelection("slide-templates");
    const handleToggle = (id: string) => {
      if (selectedIds.has(id)) {
        toggleSelected(id);
        return;
      }
      requestNewSelection(() => toggleSelected(id));
    };

    useImperativeHandle(ref, () => ({ insertSelected, clearSelection: clearSelected }), [insertSelected, clearSelected]);

    useEffect(() => {
      onSelectionStateChange?.({
        count: selectedCount,
        canInsert: selectedCount > 0 && !isInserting,
        isInserting,
        view,
      });
    }, [onSelectionStateChange, selectedCount, isInserting, view]);

    if (loadState.status === "loading") {
      return (
        <div className={styles.root}>
          <div className={styles.centered}>
            <span className={styles.spinner} aria-hidden="true" />
            <span>Loading slide templates…</span>
          </div>
        </div>
      );
    }

    if (loadState.status === "error") {
      return (
        <div className={styles.root}>
          <Notice
            icon={<WarningIcon />}
            title="Couldn't load slide templates"
            message="Check your connection and try again."
            action={
              <button type="button" className={styles.retryBtn} onClick={retry}>
                Try Again
              </button>
            }
          />
        </div>
      );
    }

    if (view === "empty") {
      return (
        <div className={styles.root}>
          <Notice
            icon={<FilesIcon />}
            title="No slide templates yet"
            message="When your organisation publishes slide templates, they'll appear here. Ask your brand administrator to add them."
          />
        </div>
      );
    }

    return (
      <div className={styles.root}>
        <div className={styles.searchArea}>
          <div className={styles.searchBox}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder={SEARCH_PLACEHOLDER}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape" && search !== "") {
                  // Swallow it so the first Esc only clears the search, not
                  // the surrounding dialog.
                  e.preventDefault();
                  e.stopPropagation();
                  clearSearch();
                }
              }}
              aria-label="Search slide templates"
            />
            {search !== "" && (
              <button
                type="button"
                className={styles.clearSearchBtn}
                aria-label="Clear search"
                title="Clear search (Esc)"
                onClick={clearSearch}
              >
                <svg aria-hidden="true" viewBox="0 0 20 20" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M5 5l10 10M15 5L5 15" />
                </svg>
              </button>
            )}
          </div>
          {trimmedSearch !== "" && (
            <output className={styles.resultCount} aria-live="polite">
              {matchLineText}
            </output>
          )}
        </div>

        {noneInsertable && (
          <div className={styles.previewOnlyNotice}>
            These layouts can be previewed but not inserted yet — the library doesn&apos;t publish slide content.
          </div>
        )}

        {insertError && <div className={styles.insertError}>{insertError}</div>}
        {/* The dialog window is the only UI in the real host, so it reports the
            insert outcome itself rather than relying on a task-pane toast. */}
        {!insertError && insertStatus && (
          <output className={styles.insertStatus}>{insertStatus}</output>
        )}

        <div className={styles.body}>
          {categoryCounts.length > 0 && (
            <CategoryFilterSidebar
              categories={categoryCounts}
              totalCount={searchMatches.length}
              activeCategory={categoryFilter}
              onSelect={setCategoryFilter}
            />
          )}

          <div className={styles.gridArea}>
            {visibleTemplates.length === 0 ? (
              <NoResults query={trimmedSearch} onClear={clearSearch} />
            ) : (
              <div className={styles.grid}>
                {visibleTemplates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    selected={selectedIds.has(template.id)}
                    showPreviewOnlyBadge={!template.insertable && !noneInsertable}
                    onToggle={handleToggle}
                    loadThumbnail={loadThumbnail}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);
